import React from "react";
import { Grid, Paper, Typography } from "@mui/material";
import { Constants } from "constants/constant";
import smoke from "../../../assets/Icons/smoke.png";
import waste from "../../../assets/Icons/waste.png";
import pollution from "../../../assets/Icons/pollution.png";
import { scopesCoveredOptions } from "../Data/PortfolioViewData";

interface TotalEmissionsByScopeProps {
  emissionValues: { [key: string]: number };
  selectedScope: number[];
  displayContext?: "portfolio" | "location";
}

/**
 * A React functional component that displays total emissions by scope.
 * It shows emission values for Scope 1, Scope 2, and Scope 3, and icons for each scope.
 *
 * @component
 * @param {Object} props - Props passed to the component.
 * @param {Object.<string, number>} props.emissionValues - Emission values for each scope, mapped by scope key (e.g., 'Scope 1', 'Scope 2').
 * @param {number[]} props.selectedScope - An array of selected scope IDs to display.
 */
const TotalEmissionsByScope: React.FC<TotalEmissionsByScopeProps> = ({
  emissionValues,
  selectedScope,
  displayContext = "portfolio",
}) => {
  /**
   * Renders the emission value for a given scope.
   *
   * @function
   * @param {string} scopeKey - The key of the scope (e.g., 'Scope 1').
   * @returns {string} - The formatted emission value as a string, or "N/A" if not available.
   */
  const renderEmissionValue = (scopeKey: string) => {
    return emissionValues[scopeKey]?.toLocaleString() ?? "N/A";
  };

  /**
   * Retrieves the corresponding icon for the given scope.
   *
   * @function
   * @param {string} scopeValue - The value of the scope (e.g., 'Scope 1', 'Scope 2').
   * @returns {string} - The path to the corresponding icon image.
   */

  const UNIT_TCO2E = Constants.UNIT_TCO2E;
  const isLocationView = displayContext === "location";
  const scopeLabelFontSize = isLocationView ? "0.66rem" : "0.7rem";

  const getScopeIcon = (scopeValue: string) => {
    switch (scopeValue) {
      case scopesCoveredOptions[0].value:
        return smoke;
      case scopesCoveredOptions[1].value:
        return pollution;
      case scopesCoveredOptions[2].value:
        return waste;
      default:
        return pollution;
    }
  };

  const selectedScopeList = scopesCoveredOptions.filter((scope) =>
    selectedScope.includes(scope.id)
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        border: "1px solid #fecbc0",
        borderRadius: "12px",
        backgroundColor: "#fef5f3",
        p: isLocationView ? 1.35 : 2.5,
      }}
    >
      <Typography
        sx={{
          color: "#b45a3f",
          fontWeight: 590,
          fontSize: "0.78rem",
          mb: isLocationView ? 0.9 : 2,
        }}
      >
        Total Emissions By Scope ({UNIT_TCO2E})
      </Typography>

      {selectedScopeList.length === 0 ? (
        <Typography sx={{ color: "#64748b", fontSize: "0.8rem", py: 2 }}>
          Select Scope in the filter
        </Typography>
      ) : (
        <Grid container spacing={isLocationView ? 1 : 1.5}>
          {selectedScopeList.map((scope) => (
            <Grid item xs={4} key={scope.id}>
              <Paper
                elevation={0}
                sx={{
                  p: isLocationView ? 1 : 1.75,
                  borderRadius: "8px",
                  border: "1px solid #fecbc0",
                  backgroundColor: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minHeight: isLocationView ? "60px" : "90px",
                  position: "relative",
                }}
              >
                <Typography
                  data-testid={scope.value}
                  sx={{
                    fontSize: isLocationView ? "1.05rem" : "1.3rem",
                    fontWeight: 700,
                    color: "#0f2b52",
                    mb: isLocationView ? 0.2 : 0.5,
                  }}
                >
                  {renderEmissionValue(scope.value)}
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: scopeLabelFontSize,
                  }}
                >
                  {scope.label}
                </Typography>
                <img
                  src={getScopeIcon(scope.value)}
                  alt={`${scope.value} icon`}
                  style={{
                    width: isLocationView ? "26px" : "34px",
                    height: isLocationView ? "26px" : "34px",
                    opacity: 0.4,
                    position: "absolute",
                    filter: "blur(0.8px)",
                    bottom: isLocationView ? 6 : 12,
                    right: isLocationView ? 6 : 12,
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
};

export default TotalEmissionsByScope;
